import { useEffect, useState } from 'react';
import { warehouseApi, type WmLaborMetricRow } from '@/api/warehouse.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { TablePagination } from '@/components/ui/TablePagination';
import { paginateClientList, usePagination } from '@/hooks/usePagination';
import { showApiError } from '@/utils/apiError';

export function WarehouseLaborPage() {
  const pagination = usePagination();
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState<WmLaborMetricRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    warehouseApi
      .laborMetrics(day)
      .then((data) => {
        setRows(data);
        pagination.applyClientCount(data.length);
        pagination.resetPage();
      })
      .catch((e) => showApiError(e, 'Failed to load labor metrics'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [day]);

  return (
    <div className="pb-safe">
      <PageHeader
        title="Labor metrics"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Warehouse', to: '/warehouse' },
          { label: 'Labor' },
        ]}
        actions={
          <Button variant="secondary" loading={loading} onClick={load}>
            Refresh
          </Button>
        }
      />

      <div className="card mb-6 max-w-xs p-4">
        <DatePicker label="Day" value={day} onChange={setDay} />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-slate-500">
              <th className="p-2">User ID</th>
              <th className="p-2">Scans</th>
              <th className="p-2">Picks completed</th>
              <th className="p-2">Putaways completed</th>
            </tr>
          </thead>
          <tbody>
            {paginateClientList(rows, pagination.page, pagination.size).map((r) => (
              <tr key={r.userId} className="border-b border-slate-100">
                <td className="p-2">{r.userId}</td>
                <td className="p-2">{r.scanCount}</td>
                <td className="p-2">{r.picksCompleted}</td>
                <td className="p-2">{r.putawaysCompleted}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !loading && (
          <p className="p-4 text-sm text-slate-500">No activity for this day.</p>
        )}
        <TablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
          loading={loading}
          hasRows={rows.length > 0}
        />
      </div>
    </div>
  );
}
